/**
 * The one place either app uploads a file (016 research.md R12): every upload goes to the Files
 * service (`POST /api/files/{kind}s`), which answers with a content hash the caller then hands to
 * Catalog or Warehouse. Contract: `../Steelyard/specs/016-files-service/contracts/files-api.md`.
 */

/** The route segment, singular: `uploadFile` posts to `/api/files/${kind}s`. */
export type UploadKind = "image" | "catalogue-import" | "stock-upload";

/** Files' 201 body. `kind` is the wire value (snake_case), not the route segment. */
export type UploadedFile = {
  hash: string;
  kind: "image" | "catalogue_import" | "stock_upload";
  contentType: string;
  sizeBytes: number;
};

/** RFC 9457 problem document, as Files answers every 4xx/5xx. */
export type UploadProblem = { type?: string; title?: string; status?: number; detail?: string; [member: string]: unknown };

/** Thrown on any non-2xx upload. Same fields as admin's `ApiError`, so `ErrorSurface` renders it alike. */
export class UploadError extends Error {
  status: number;
  title?: string;
  detail?: string;
  type?: string;
  raw: UploadProblem | null;

  constructor(status: number, problem: UploadProblem | null) {
    super(problem?.title ?? `Upload failed with status ${status}.`);
    this.status = status;
    this.title = problem?.title;
    this.detail = problem?.detail;
    this.type = problem?.type;
    this.raw = problem;
  }
}

export async function uploadFile(kind: UploadKind, file: File, { baseUrl = "" }: { baseUrl?: string } = {}): Promise<UploadedFile> {
  const body = new FormData();
  body.append("file", file);
  const response = await fetch(`${baseUrl}/api/files/${kind}s`, { method: "POST", body, credentials: "include" });
  if (!response.ok) throw new UploadError(response.status, await response.json().catch(() => null));
  return (await response.json()) as UploadedFile;
}

export function uploadFiles(kind: UploadKind, files: File[], options?: { baseUrl?: string }): Promise<UploadedFile[]> {
  return Promise.all(files.map((file) => uploadFile(kind, file, options)));
}

/** Where an uploaded image is read from. Admin reads Files directly; the portal passes its own `/images` route. */
export function imageUrl(hash: string, base = "/api/files/images"): string {
  return `${base}/${encodeURIComponent(hash)}`;
}
